import { createSession } from '@/actions/auth';
import { authenticatedFetch } from '@/lib/autheticatedFetch';
import { CreditCard, Customer, User, UserRole } from '@/types/Auth';
import { deleteCookie } from 'cookies-next';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';

export interface UserContextProps {
  user: User;
  customer: Customer;
  setUser: Dispatch<SetStateAction<User>>;
  isLogged: boolean;
  setIsLogged: Dispatch<SetStateAction<boolean>>;
  userGrant: string;
  setLoggedCustomer: (loggedCustomer: Customer) => void;
  getCreditCards: () => Promise<CreditCard[]>;
  setUserGrant: Dispatch<SetStateAction<string>>;
  logout(): Promise<void>;
  saveUserInfos(user: User): void;
  removeUserInfos(): void;
}

interface UserProviderProps {
  children: ReactNode;
}

export const UserContext = createContext<UserContextProps>(
  {} as UserContextProps,
);

const defaultUser = {
  id: 0,
  username: '',
  emailCanonical: '',
  enabled: false,
  user_grant: 'none',
  user_type: null,
  fullAccess: false,
  firstName: '',
  lastName: '',
  timestampAcceptedTerms: null,
  media: null,
};

export const UserContextProvider: FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>(defaultUser);
  const [customer, setCustomer] = useState<Customer>({} as Customer);
  const [isLogged, setIsLogged] = useState(false);
  const [userGrant, setUserGrant] = useState<string>(defaultUser.user_grant);

  async function logout() {
    setUser(defaultUser);
    setIsLogged(false);
    deleteCookie('PHPSESSID');
    deleteCookie('access_token');
    deleteCookie('sessionId');
    deleteCookie('session');

    await createSession();
    setCustomer({} as Customer);
    setUserGrant(defaultUser.user_grant);
    removeUserInfos();
  }

  function saveUserInfos(user: User) {
    window.localStorage.setItem('user-infos', JSON.stringify(user));
  }

  function removeUserInfos() {
    window.localStorage.removeItem('user-infos');
  }

  function setLoggedCustomer(loggedCustomer: Customer) {
    setCustomer(loggedCustomer);
  }

  async function getCreditCards() {
    if (isLogged) {
      const creditCards = await authenticatedFetch<CreditCard[]>({
        url: `/payment/methods`,
        method: 'GET',
      });

      return creditCards.data;
    }

    return [];
  }

  function setUserFromStorage() {
    if (typeof window !== 'undefined') {
      try {
        const userString = window.localStorage.getItem('user-infos');

        const userInfos: User | null = userString
          ? JSON.parse(userString)
          : null;
        if (userInfos) {
          setUser(userInfos);
          setIsLogged(true);
          // setLoggedCustomer();
          setUserGrant(userInfos.user_grant);
        }
      } catch (error) {
        if (error instanceof Error) {
          console.log(`JSPERROR:104:${error.message}`);
        } else {
          console.log('JSPERROR:104', error);
        }
      }
    }
  }

  useEffect(() => {
    setUserFromStorage();
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        setLoggedCustomer,
        customer,
        getCreditCards,
        setUser,
        isLogged,
        setIsLogged,
        userGrant,
        setUserGrant,
        logout,
        saveUserInfos,
        removeUserInfos,
      }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
